import PropTypes from 'prop-types'
import React, {useEffect, useMemo, useState} from 'react'
import {Text, Heading, HStack, Box, Image, Container} from '@chakra-ui/react'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {useIntl} from 'react-intl'
import CloudinaryImage from '../../cloudinary-media/image'

const LARGE = 'large'

const StickyBar = ({
    product,
    variationAttributes,
    renderButton,
    isSet,
    priceData,
    variant,
    priceDataSets,
    selectedVariationAttributes,
    imageGroups = []
}) => {
    const [isSticky, setIsSticky] = useState(false)
    const intl = useIntl()
    const images = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes
            })?.images,
        [selectedVariationAttributes]
    )

    const handleScroll = () => {
        if (window.innerWidth < 992) {
            const el =
                document.querySelector('.add-to-cart-wrapper') ||
                document.querySelector('#product-notify-form button') ||
                document.querySelector('#attn-waitlist-trigger button') ||
                document.querySelector('#attn-waitlist-trigger')
            if (!el) return
            const rect = el.getBoundingClientRect()
            const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight
            setIsSticky(!isInView)
        } else {
            const stickyOffset = document
                .querySelector('.sticky-bar-reference')
                ?.getBoundingClientRect()?.bottom
            if (stickyOffset - 60 < 0) {
                setIsSticky(true)
            } else {
                setIsSticky(false)
            }
        }
    }

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, {passive: true})
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    useEffect(() => {
        handleScroll()
    }, [product?.id])

    const imageLink = variationAttributes?.[0]?.values?.find(
        (v) => v.value === variationAttributes?.[0]?.selectedValue.value
    )?.image?.disBaseLink

    return (
        <Container
            spacing={4}
            padding="0"
            className={`${isSticky ? 'sticky buy-now-wrapper' : 'buy-now-wrapper'}`}
            zIndex="11" // Reviews have "10"
            position="fixed"
            bottom="0"
            top="unset"
            transform={isSticky ? 'translateY(0)' : 'translateY(100%)'}
            transition="transform 0.35s ease"
            visibility={isSticky ? 'visible' : 'hidden'}
            pointerEvents={isSticky ? 'auto' : 'none'}
            aria-hidden={!isSticky}
            maxWidth="100%"
        >
            <HStack
                className="sticky-bar-stack"
                justifyContent="space-between"
                background="black.300"
                height={{base: '80px', lg: '100px'}}
                padding={{base: '0 10px', lg: '0 60px'}}
                width="100%"
                spacing={2}
                margin="auto"
            >
                <Container
                    padding="0"
                    className="sticky-bar-container"
                    maxWidth={{
                        base: isSet ? 'calc(100% - 192px)' : 'calc(100% - 150px)',
                        lg: '39%',
                        xl: '54%'
                    }}
                    margin="0"
                    display={{base: 'none', lg: 'inline-flex'}}
                >
                    {images?.[0]?.disBaseLink && (
                        <Box className="image-parent" display={{base: 'none', lg: 'block'}}>
                            <CloudinaryImage
                                src={images[0].disBaseLink}
                                alt={images[0]?.alt}
                                aspectRatio="54:68"
                                imgSizes={{base: '54px'}}
                                sizes={[54, 108, 162]}
                                styles={{
                                    aspectRatio: '54/68',
                                    width: '54px',
                                    height: '68px',
                                    htmlWidth: 54,
                                    htmlHeight: 68
                                }}
                            />
                        </Box>
                    )}
                    <Box
                        alignContent="center"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        className="sticky-product-name"
                    >
                        <Heading
                            as="h2"
                            fontSize="23px"
                            fontWeight="400"
                            lineHeight="30px"
                            color="white"
                            marginLeft={{base: '5px', lg: '10px'}}
                            className="buy-now-name"
                            whiteSpace="nowrap"
                            alignContent="center"
                            overflow="hidden"
                            textOverflow="ellipsis"
                        >
                            {product?.name}
                        </Heading>
                        {((priceData?.currentPrice && product?.type?.set) || variant?.price) && (
                            <Text
                                marginLeft={{base: '5px', lg: '10px'}}
                                fontSize="17px"
                                fontWeight="300"
                                className="sticky-bar-price"
                                fontFamily="body"
                                color="white"
                            >
                                {product?.c_selectedCurrency &&
                                    product?.c_selectedCurrencySymbol &&
                                    intl
                                        .formatNumber(
                                            isSet
                                                ? priceDataSets?.currentPrice
                                                : priceData?.currentPrice || variant?.price,
                                            {
                                                style: 'currency',
                                                currency: product?.c_selectedCurrency
                                            }
                                        )
                                        ?.replace(
                                            product?.c_selectedCurrency,
                                            product?.c_selectedCurrencySymbol
                                        )}
                            </Text>
                        )}
                    </Box>
                </Container>
                <Box
                    className="left-section-sticky"
                    gap="30px"
                    width={{base: '100%', lg: 'unset'}}
                    marginLeft={{base: '0', lg: '60px'}}
                    sx={{
                        '& button': {
                            padding: {base: '7px 25px', lg: '15px 50px'},
                            width: 'fit-content',
                            height: 'auto',
                            marginBottom: '0',
                            color: 'black.100',
                            minWidth: {base: '100%', lg: '191px'}
                        },
                        '.tick-icon': {
                            filter: 'invert(1)'
                        },
                        'button:not(:disabled)': {
                            backgroundColor: 'white',
                            color: 'black.100',
                            _hover: {
                                backgroundColor: 'gray.425'
                            }
                        }
                    }}
                >
                    {variationAttributes?.[0]?.selectedValue && (
                        <Box
                            flexWrap="wrap"
                            alignContent="center"
                            className="sticky-bar-variation"
                            gap="15px"
                            display={{
                                base: 'none',
                                lg: variationAttributes?.[0]?.selectedValue?.name
                                    ? 'inline-flex'
                                    : 'none'
                            }}
                            marginRight="30px"
                        >
                            <Text
                                color="white"
                                fontWeight="400"
                                lineHeight="25px"
                                className="sticky-bar-text"
                                fontSize="16px"
                                alignContent="center"
                            >
                                {variationAttributes?.[0]?.name}
                            </Text>
                            <Text
                                color="white"
                                fontWeight="300"
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                className="sticky-bar-text"
                                maxWidth="118px"
                                lineHeight="25px"
                                fontSize="16px"
                                alignContent="center"
                            >
                                {variationAttributes?.[0]?.selectedValue?.name}
                            </Text>
                            {imageLink && (
                                <Box
                                    border="1px solid"
                                    borderColor="white"
                                    padding="3px"
                                    className="sticky-bar-box"
                                    cursor="pointer"
                                    onClick={() => {
                                        const element = document.querySelector('.color-label')
                                        if (
                                            element &&
                                            variationAttributes?.[0]?.selectedValue?.name
                                        ) {
                                            const offset =
                                                element.getBoundingClientRect().top +
                                                window.pageYOffset -
                                                250
                                            window.scrollTo({
                                                top: offset,
                                                behavior: 'smooth'
                                            })
                                        }
                                    }}
                                    borderRadius="50%"
                                >
                                    <Image
                                        borderRadius="50%"
                                        width="35px"
                                        height="35px"
                                        className="sticky-bar-image"
                                        loading="lazy"
                                        alt={variationAttributes?.[0]?.selectedValue?.name}
                                        src={imageLink}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                    {renderButton()}
                </Box>
            </HStack>
        </Container>
    )
}

StickyBar.propTypes = {
    product: PropTypes.object,
    variationAttributes: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            selectedValue: PropTypes.shape({
                name: PropTypes.string,
                value: PropTypes.string
            }),
            values: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.string,
                    image: PropTypes.shape({
                        disBaseLink: PropTypes.string,
                        alt: PropTypes.string
                    })
                })
            )
        })
    ),
    renderButton: PropTypes.func,
    priceData: PropTypes.any,
    priceDataSets: PropTypes.any,
    isSet: PropTypes.bool,
    selectedVariationAttributes: PropTypes.object,
    variant: PropTypes.object,
    imageGroups: PropTypes.array
}
export default StickyBar
